import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert as MuiAlert,
  Button,
  DialogActions,
  Box,
  CircularProgress,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useDialog } from "../../../../contexts/DialogContext";
import usePlan from "../../../../hooks/usePlan";
import SearchableAutoComplete from "../../../../components/SearchableAutoComplete";
import usePlanGroup from "../../../../hooks/usePlanGroup";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import CustomTextField from "../../../../components/CustomTextField";
import CustomCheckBox from "../../../../components/CustomCheckBox";
import { useTranslation } from "react-i18next";

const Alert = styled(MuiAlert)(spacing);

const MapPlanModal = ({
  planGroupId,
  isNew,
  isSharingAllowed,
  planGroupPlanId,
  isEditable = false,
  actionName = "Add",
  multiple,
  sharingType,
  msoSharing,
  distSharing,
  lcoSharing,
  serviceType,
  setPlanGroupPlanValues,
  planGroupPlans,
}) => {
  const initialValues = {
    planName: multiple ? [] : null,
    msoSharing: msoSharing,
    distSharing: distSharing,
    lcoSharing: lcoSharing,
    isSharingAllowed: true,
  };

  console.log(sharingType, "sharingType9090");

  const validationSchema = Yup.object().shape({
    planName: multiple
      ? Yup.array()
          .of(
            Yup.object().shape({
              label: Yup.string().required(),
              value: Yup.number().required(),
            }),
          )
          .min(1, "You need at least one plan")
      : Yup.object()
          .shape({
            label: Yup.string().required(),
            value: Yup.number().required(),
          })
          .required("Please select the plan"),
    msoSharing: Yup.number().when("isSharingAllowed", {
      is: true,
      then: () =>
        Yup.number()
          .typeError("MSO sharing must be a number")
          .min(0, "Minimum value should be 0")
          .max(100, "Maximum value should be 100")
          .required(),
    }),
    distSharing: Yup.number().when("isSharingAllowed", {
      is: true,
      then: () =>
        Yup.number()
          .typeError("Dist sharing must be a number")
          .min(0, "Minimum value should be 0")
          .max(100, "Maximum value should be 100")
          .required(),
    }),
    lcoSharing: Yup.number().when("isSharingAllowed", {
      is: true,
      then: () =>
        Yup.number()
          .typeError("LCO sharing must be a number")
          .min(0, "Minimum value should be 0")
          .max(100, "Maximum value should be 100")
          .required(),
    }),
  });
  const { unSetDialog } = useDialog();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const { autoSearchPlan, planAutoSearchResults, autoSearchLoading, getPlan, plan } = usePlan();
  const { addPlanGroupPlan, isSubmitting, getPlanGroupPlans, getPlanGroupPlan, planGroupPlan } =
    usePlanGroup();
  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  };

  const [payload, setPayload] = useState(currentPayload);

  const [planGroupPlanPayload, setPlanGroupPlanPayload] = useState({
    filters: [
      {
        fieldName: "planGroupId",
        fieldValue: planGroupId,
        op: "eq",
        isJoin: true,
        dataType: "long",
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  });

  const [planOptions, setPlanOptions] = useState([]);

  const fetchData = async () => {
    await autoSearchPlan(payload);
  };

  const isFieldDisabled = () => {
    console.log(sharingType, "hellowr");
    return sharingType === "FIXED";
  };

  const fetchPlanGroupPlan = async () => {
    await getPlanGroupPlan(planGroupPlanId);
  };

  useEffect(() => {
    if (planAutoSearchResults.data.length !== 0) {
      setPlanOptions([...planOptions, ...planAutoSearchResults.data]);
    }
  }, [planAutoSearchResults]);

  useEffect(() => {
    fetchData();
  }, [payload]);

  useEffect(() => {
    if (planGroupPlanId != undefined) {
      fetchPlanGroupPlan();
    }
  }, []);

  useEffect(() => {
    console.log(isEditable, planGroupPlan, initialValues, "planGroupPlanllsoff");
    if (planGroupPlan != undefined && Object.keys(planGroupPlan).length != 0 && isEditable) {
      initialValues["planName"] = [
        { label: planGroupPlan.plan?.planName, value: planGroupPlan.plan?.planId },
      ];
      initialValues["planGroupPlanId"] = planGroupPlan?.planGroupPlanId;
      initialValues["msoSharing"] = planGroupPlan.msoSharing;
      initialValues["distSharing"] = planGroupPlan.distSharing;
      initialValues["lcoSharing"] = planGroupPlan.lcoSharing;
      initialValues["isSharingAllowed"] = planGroupPlan.isSharingAllowed;
      reset(initialValues);
    } else {
      reset(initialValues);
    }
  }, [planGroupPlan]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const selectedPlan = watch("planName");

  const fetchPlanDetails = async () => {
    await getPlan(selectedPlan?.value);
  };

  useEffect(() => {
    console.log(selectedPlan, "selectedPlan");
    if (selectedPlan != null && !Array.isArray(selectedPlan)) {
      fetchPlanDetails();
    }
  }, [selectedPlan]);

  const onSubmit = async (data) => {
    try {
      const planGroupDetails = [];
      const totalSharing = data.msoSharing + data.distSharing + data.lcoSharing;

      if (totalSharing != 100 && isSharingAllowed) {
        dispatch(showErrorSnackbar("Sharing should be equal to 100"));
        return;
      }

      const plans = data.planName;
      if (Array.isArray(plans)) {
        for (let i = 0; i < plans.length; i++) {
          const planDetails = await getPlan(plans[i]["value"]);

          if (planExists(plans[i]["value"])) {
            dispatch(showErrorSnackbar("Plan already exists"));
            return;
          }

          planGroupDetails.push({
            planGroupPlanId: data["planGroupPlanId"],
            planGroup: {
              planGroupId: planGroupId,
            },
            planId: plans[i]["value"],
            planName: plans[i]["label"],
            msoSharing: data.msoSharing,
            distSharing: data.distSharing,
            lcoSharing: data.lcoSharing,
            serviceType: serviceType,
            isSharingAllowed: isSharingAllowed,
            sharingType: sharingType,
          });
        }
      } else {
        if (planExists(data.planName["value"])) {
          dispatch(showErrorSnackbar("Plan already exists"));
          return;
        }
        const planDetails = await getPlan(data.planName["value"]);
        planGroupDetails.push({
          planGroupPlanId: data["planGroupPlanId"],
          planGroup: {
            planGroupId: planGroupId,
          },
          planId: data.planName["value"],
          planName: data.planName["label"],
          msoSharing: data.msoSharing,
          distSharing: data.distSharing,
          lcoSharing: data.lcoSharing,
        });
      }

      // await addPlanGroupPlan(planGroupDetails);
      // await getPlanGroupPlans(planGroupPlanPayload);
      // reset();
      setPlanGroupPlanValues("planGroupPlans", [...planGroupPlans, ...planGroupDetails]);
      dispatch(showSuccessSnackbar("Plan mapped successfully!"));
      unSetDialog("map_plan_modal");
    } catch (err) {
      console.log(err, "errerrerr");
      dispatch(showErrorSnackbar(err.message));
    }
  };
  const planExists = (planId) => {
    return planGroupPlans.some((p) => p.planId === planId);
  };
  const searchPlanInputChange = (data) => {
    let filters = [];
    filters.push({
      fieldName: "planName",
      fieldValue: data,
      op: "like",
      dataType: "string",
      condition: "and",
      isJoin: false,
    });
    setPayload({
      ...payload,
      filters: filters,
      pagination: {
        pageNumber: 1,
        pageSize: 100,
      },
    });
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: 10 }}>
            <Grid container spacing={12} className="row" pt={8}>
              <Grid item xs={10} sm={12} md={4} lg={4} xl={4}>
                <SearchableAutoComplete
                  control={control}
                  data={planOptions}
                  label={t("plan_name")}
                  payload={payload}
                  setPayload={setPayload}
                  loading={autoSearchLoading}
                  name="planName"
                  multiple={multiple}
                  hasNextPage={planAutoSearchResults.paging.hasNextPage}
                  disabled={isEditable}
                  fields={validationSchema.fields}
                  searchInputChange={searchPlanInputChange}
                />
              </Grid>
            </Grid>
            {isSharingAllowed && (
              <Grid container spacing={12} className="row" pt={8}>
                <Grid item md={3}>
                  <CustomTextField
                    label={t("mso_sharing")}
                    name="msoSharing"
                    type="text"
                    my={2}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationSchema.fields}
                    disabled={isFieldDisabled()}
                  />
                </Grid>

                <Grid item md={3}>
                  <CustomTextField
                    label={t("dist_sharing")}
                    name="distSharing"
                    type="text"
                    my={2}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationSchema.fields}
                    disabled={isFieldDisabled()}
                  />
                </Grid>

                <Grid item md={3}>
                  <CustomTextField
                    label={t("lco_sharing")}
                    name="lcoSharing"
                    type="text"
                    my={2}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationSchema.fields}
                    disabled={isFieldDisabled()}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
          <DialogActions>
            <Button
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              endIcon={isSubmitting ? <CircularProgress size={24} /> : null}
            >
              {actionName}
            </Button>

            <Button variant="text" onClick={() => unSetDialog("map_plan_modal")} color="primary">
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
export default MapPlanModal;
